/**
 * scenarioVerdict.ts
 *
 * Pure, unit-testbare Tail-Metrik- und Verdict-Funktionen für die Szenario-Matrix
 * („Wann gewinnt der 3-Topf?"). Keine Engine-/React-Abhängigkeiten.
 *
 * Kernidee: Der 3-Topf wird nicht am Mittelwert, sondern am TAIL gemessen —
 * schlechte 5-/10-%-Ausgänge (P5/P10-Endkapital) und Verarmungsrisiko. Verarmte
 * Läufe zählen dabei als 0 € Endkapital (Verarmt-⇒-0-Regel), nicht als „aus der
 * Statistik entfernt". Ein Sieg entsteht nur, wenn der Vorteil größer als das
 * statistische Rauschen ist (Epsilon-Ties).
 */

import { StatisticalHelpers } from './statisticalHelpers';
import {
  ScenarioAxisWinner,
  ScenarioCellResult,
  ScenarioStrategyTailMetrics,
  ScenarioVerdict,
} from '../types/simulation';
import type { CombinedMonteCarloResult } from '../services/CombinedMonteCarloEngine';

// Epsilon-Konstanten: Vorteile unterhalb dieser Schwellen gelten als Gleichstand
// (tie), um statistisches Rauschen bei moderater Iterationszahl nicht als „Sieg"
// zu verkaufen.
export const P10_RELATIVE_EPSILON = 0.005; // ≤ 0,5 % relativ zum größeren P10 → tie
export const RISK_PP_EPSILON = 0.2;        // ≤ 0,2 Prozentpunkte Risiko-Differenz → tie

type StrategyKey = 'threeTopf' | 'oneTopf';

/**
 * Berechnet die Tail-Metriken einer Strategie über alle Läufe einer Zelle.
 *
 * Endkapital-Regel: Verarmte Läufe (!successfulStrategy) zählen als 0 €, nicht
 * ausgeschlossen. Erfolgreiche Läufe zählen mit max(0, finalBalance).
 *
 * Hintergrund `finalBalance`: In der Engine ist finalBalance = gesamtsaldo des
 * letzten simulierten Jahres. Für gescheiterte Läufe (früh erschöpft ODER Zieljahr
 * unterfinanziert) ist das der Restsaldo bei Erschöpfung bzw. im unterfinanzierten
 * Zieljahr — NICHT garantiert 0. Das explizite 0-Mapping über das
 * successfulStrategy-Flag macht die Tail-Metrik unabhängig von dieser Semantik.
 */
export function computeStrategyTailMetrics(
  results: CombinedMonteCarloResult[],
  strategy: StrategyKey
): ScenarioStrategyTailMetrics {
  const n = results.length;
  if (n === 0) {
    return { depletionRisk: 0, p5: 0, p10: 0, median: 0, avgYearsFunded: 0 };
  }

  // Endkapitale mit Verarmt-⇒-0-Regel.
  const endCapitals = results.map(r =>
    r[strategy].successfulStrategy ? Math.max(0, r[strategy].finalBalance) : 0
  );

  // Verarmungsrisiko: Anteil der nicht erfolgreichen Läufe in Prozent.
  const depletions = results.filter(r => !r[strategy].successfulStrategy).length;
  const depletionRisk = (depletions / n) * 100;

  // Finanzierte Jahre = jahresergebnisse.length - 1 (Jahr 0 = Rentenbeginn zählt nicht).
  const yearsFunded = results.map(r =>
    Math.max(0, r[strategy].jahresergebnisse.length - 1)
  );

  return {
    depletionRisk,
    p5: StatisticalHelpers.calculatePercentile(endCapitals, 5),
    p10: StatisticalHelpers.calculatePercentile(endCapitals, 10),
    median: StatisticalHelpers.calculatePercentile(endCapitals, 50),
    avgYearsFunded: StatisticalHelpers.calculateMean(yearsFunded),
  };
}

/**
 * P10-Achse: welche Strategie hat das höhere P10-Endkapital?
 * Tie, wenn beide P10 = 0 (degenerierte Zelle) oder die relative Differenz
 * ≤ P10_RELATIVE_EPSILON ist.
 */
export function computeP10Axis(
  threeTopf: ScenarioStrategyTailMetrics,
  oneTopf: ScenarioStrategyTailMetrics
): ScenarioAxisWinner {
  const scale = Math.max(threeTopf.p10, oneTopf.p10);
  if (scale <= 0) {
    return 'tie';
  }
  const delta = threeTopf.p10 - oneTopf.p10;
  if (Math.abs(delta) <= P10_RELATIVE_EPSILON * scale) {
    return 'tie';
  }
  return delta > 0 ? 'threeTopf' : 'oneTopf';
}

/**
 * Risiko-Achse: welche Strategie hat das niedrigere Verarmungsrisiko?
 * Tie, wenn die absolute Differenz ≤ RISK_PP_EPSILON Prozentpunkte ist.
 */
export function computeRiskAxis(
  threeTopf: ScenarioStrategyTailMetrics,
  oneTopf: ScenarioStrategyTailMetrics
): ScenarioAxisWinner {
  const delta = threeTopf.depletionRisk - oneTopf.depletionRisk;
  if (Math.abs(delta) <= RISK_PP_EPSILON) {
    return 'tie';
  }
  // Niedrigeres Risiko gewinnt.
  return delta < 0 ? 'threeTopf' : 'oneTopf';
}

/**
 * Deutscher Kompakt-Text zum Urteil einer Zelle.
 * Beispiele: „P10 +14 % / Risiko −1,8 pp", „P10 gleichauf, Risiko niedriger".
 */
function buildVerdictReason(
  threeTopf: ScenarioStrategyTailMetrics,
  oneTopf: ScenarioStrategyTailMetrics,
  p10Axis: ScenarioAxisWinner,
  riskAxis: ScenarioAxisWinner
): string {
  // P10-Teil (relativ, aus Sicht des 3-Topfs).
  let p10Part: string;
  if (p10Axis === 'tie') {
    p10Part = 'P10 gleichauf';
  } else {
    const scale = Math.max(threeTopf.p10, oneTopf.p10);
    const relPct = scale > 0 ? ((threeTopf.p10 - oneTopf.p10) / scale) * 100 : 0;
    const sign = relPct >= 0 ? '+' : '−';
    p10Part = `P10 ${sign}${Math.abs(relPct).toFixed(0)} %`;
  }

  // Risiko-Teil (Prozentpunkte, aus Sicht des 3-Topfs: negativ = 3-Topf riskoärmer).
  let riskPart: string;
  const riskDeltaPp = threeTopf.depletionRisk - oneTopf.depletionRisk;
  if (riskAxis === 'tie') {
    riskPart = 'Risiko gleichauf';
  } else {
    const sign = riskDeltaPp >= 0 ? '+' : '−';
    riskPart = `Risiko ${sign}${Math.abs(riskDeltaPp).toFixed(1).replace('.', ',')} pp`;
  }

  return `${p10Part} / ${riskPart}`;
}

/**
 * Gesamturteil einer Zelle aus den beiden Achsen-Siegern.
 *
 *   beide tie                  → 'unentschieden'  (deckt keine-rendite-Zellen ab)
 *   nur 3-Topf-Achsen-Siege    → '3topf'
 *   nur 1-Topf-Achsen-Siege    → '1topf'
 *   je eine Achse pro Strategie → 'gemischt'
 */
export function computeScenarioVerdict(
  threeTopf: ScenarioStrategyTailMetrics,
  oneTopf: ScenarioStrategyTailMetrics
): {
  p10Axis: ScenarioAxisWinner;
  riskAxis: ScenarioAxisWinner;
  verdict: ScenarioVerdict;
  verdictReason: string;
} {
  const p10Axis = computeP10Axis(threeTopf, oneTopf);
  const riskAxis = computeRiskAxis(threeTopf, oneTopf);

  const axes = [p10Axis, riskAxis];
  const threeWins = axes.filter(a => a === 'threeTopf').length;
  const oneWins = axes.filter(a => a === 'oneTopf').length;

  let verdict: ScenarioVerdict;
  if (threeWins === 0 && oneWins === 0) {
    verdict = 'unentschieden';
  } else if (threeWins > 0 && oneWins === 0) {
    verdict = '3topf';
  } else if (oneWins > 0 && threeWins === 0) {
    verdict = '1topf';
  } else {
    verdict = 'gemischt';
  }

  const verdictReason = buildVerdictReason(threeTopf, oneTopf, p10Axis, riskAxis);
  return { p10Axis, riskAxis, verdict, verdictReason };
}

/**
 * Baut ein vollständiges Zell-Ergebnis aus den Rohläufen einer Zelle.
 * Bündelt Tail-Metriken + Verdict für Engine und Tests.
 */
export function buildScenarioCellResult(
  results: CombinedMonteCarloResult[],
  inflationOption: string,
  investmentReturnOption: string,
  withdrawalRate: number
): ScenarioCellResult {
  const threeTopf = computeStrategyTailMetrics(results, 'threeTopf');
  const oneTopf = computeStrategyTailMetrics(results, 'oneTopf');
  const { p10Axis, riskAxis, verdict, verdictReason } = computeScenarioVerdict(
    threeTopf,
    oneTopf
  );

  return {
    inflationOption,
    investmentReturnOption,
    withdrawalRate,
    iterations: results.length,
    threeTopf,
    oneTopf,
    p10Axis,
    riskAxis,
    verdict,
    verdictReason,
  };
}
